const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_TIME_PATTERN =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:\d{2})?$/;

const jstDateFormatter = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const jstDateTimeFormatter = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

interface DisplayDateParts {
  year: string;
  month: string;
  day: string;
}

function parseIsoDate(value: string): DisplayDateParts | null {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return null;

  const [, year = "", month = "", day = ""] = match;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) return null;

  return { year, month, day };
}

function getPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((part) => part.type === type)?.value ?? "";
}

function formatDateParts(parts: Intl.DateTimeFormatPart[]): string {
  return `${getPart(parts, "year")}/${getPart(parts, "month")}/${getPart(parts, "day")}`;
}

function parseDateTime(value: string): Date | null {
  const match = ISO_DATE_TIME_PATTERN.exec(value);
  if (!match) return null;

  const [, date = "", hour = "", minute = "", second = "00", fraction, zone] = match;
  const offset = zone?.match(/^([+-])(\d{2}):(\d{2})$/);
  if (
    !parseIsoDate(date) ||
    Number(hour) > 23 ||
    Number(minute) > 59 ||
    Number(second) > 59 ||
    (offset && (Number(offset[2]) > 23 || Number(offset[3]) > 59))
  )
    return null;

  const normalizedFraction = fraction ? `.${fraction.slice(0, 3).padEnd(3, "0")}` : "";
  const parsed = new Date(
    `${date}T${hour}:${minute}:${second}${normalizedFraction}${zone ?? "+09:00"}`,
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * 利用者向けに日付を `YYYY/MM/DD` で表示する。
 * 日付のみの値はタイムゾーン変換せず、日時は JST の日付へ変換する。
 */
export function formatDisplayDate(value: string | null | undefined): string {
  if (!value) return "—";

  const dateParts = parseIsoDate(value);
  if (dateParts) return `${dateParts.year}/${dateParts.month}/${dateParts.day}`;
  if (ISO_DATE_PATTERN.test(value)) return "—";

  const parsed = parseDateTime(value);
  return parsed ? formatDateParts(jstDateFormatter.formatToParts(parsed)) : "—";
}

/** 利用者向けに日時を JST の `YYYY/MM/DD HH:mm` で表示する。 */
export function formatDisplayDateTime(value: string | null | undefined): string {
  if (!value) return "—";

  const dateParts = parseIsoDate(value);
  if (dateParts) return `${dateParts.year}/${dateParts.month}/${dateParts.day} 00:00`;
  if (ISO_DATE_PATTERN.test(value)) return "—";

  const parsed = parseDateTime(value);
  if (!parsed) return "—";

  const parts = jstDateTimeFormatter.formatToParts(parsed);
  return `${formatDateParts(parts)} ${getPart(parts, "hour")}:${getPart(parts, "minute")}`;
}

/**
 * ISO 日付 (`YYYY-MM-DD`) の曜日 (0=日曜 … 6=土曜)。日付として不正な値は null。
 *
 * 日付のみの値に時差を持ち込まないよう **UTC で解釈する**。ローカルタイムで解釈すると
 * 実行環境が UTC より西のとき前日の曜日になり、稼働日判定 (#708) が 1 日ずれる。
 */
export function isoDateWeekday(value: string): number | null {
  if (!parseIsoDate(value)) return null;
  return new Date(`${value}T00:00:00.000Z`).getUTCDay();
}

/** ISO 日付 (`YYYY-MM-DD`) が土曜・日曜か。日付として不正な値は false。 */
export function isWeekendDate(value: string): boolean {
  const weekday = isoDateWeekday(value);
  return weekday === 0 || weekday === 6;
}
